import { DomainError } from "@econ-v1/rpc";
import { syncThemeClasses } from "../shell/theme-init.js";
import { getSkinStyleSheet, SKIN_VERSION } from "../skin/skin-stylesheet.js";
import { CompositionManager } from "./composition-manager.js";
import { createStageContext } from "./stage-context.js";
import { StageRegistryStore } from "./stage-registry.js";
export class StageLoader {
    #client;
    #composition;
    #registry;
    constructor(options) {
        this.#client = options.client;
        this.#registry = options.registry;
        const compositionOptions = {
            loader: this,
            registry: options.registry,
            ...(options.composition === undefined ? {} : { limits: options.composition }),
        };
        this.#composition = new CompositionManager(compositionOptions);
    }
    async load(appName, root, navigate, canCommit = () => true) {
        return await this.#composition.load(appName, root, navigate, canCommit);
    }
    async loadRaw(appName, root, navigate, canCommit, options) {
        const entry = this.#registry.entry(appName);
        if (!entry) {
            throw new DomainError({ code: "not_found", message: `Stage ${appName} is not installed` });
        }
        const prepared = await this.#client.prepareStage(appName);
        if (prepared.appName !== entry.appName ||
            prepared.manifestSha256 !== entry.manifestSha256 ||
            prepared.version !== entry.manifest.version) {
            throw new DomainError({
                code: "conflict",
                message: "Prepared stage does not match the installed manifest",
            });
        }
        const module = await this.#importModule(prepared.entryUrl);
        const session = await this.#client.openStageSession(appName, entry.manifestSha256);
        const hostRoot = document.createElement("div");
        hostRoot.dataset.stageHost = entry.appName;
        hostRoot.dataset.skinVersion = SKIN_VERSION;
        // Every stage — top-level route or composed child (both paths flow through this one
        // `loadRaw`, see `CompositionManager`) — gets its own shadow root that adopts the shared
        // skin stylesheet. `:root`-scoped tokens (`getSkinGlobalSheet`, adopted at the document
        // level in `system/pwa/src/main.ts`) inherit through shadow boundaries regardless, but the
        // skin's component *classes* only apply inside a root that has adopted the sheet — this is
        // what lets a third-party stage reuse the shared chrome classes while staying isolated
        // (a stage's own styles never leak out, and the shell's never leak in unexpectedly beyond
        // the token/class surface it opts into). `ctx.host.root` stays the inner `stageRoot`, not
        // the shadow-hosting `hostRoot` itself — the frozen `StageContext.host.root` contract
        // (`CLIENT_NODE_STAGE_CONTRACT.md` §3) only promises "an Element the host owns"; which
        // element that is remains a host-side implementation detail.
        //
        // `syncThemeClasses` carries the shell's current `dark`/`menu-dark` resolution (set on
        // `document.documentElement` by `shell/theme-init.ts`) onto `hostRoot` itself — without
        // it, `hostRoot` starts with no theme class of its own and the adopted sheet's
        // unconditional light-mode token defaults would permanently win over the shell's actual
        // theme (see `syncThemeClasses`'s own doc comment for why). A stage is recreated on every
        // mount (fresh `loadRaw` call per navigation), so this one-time sync at creation time is
        // sufficient — it always reflects the theme current at mount time.
        syncThemeClasses(hostRoot);
        const stageShadow = hostRoot.attachShadow({ mode: "open" });
        stageShadow.adoptedStyleSheets = [getSkinStyleSheet()];
        const stageRoot = document.createElement("div");
        stageRoot.dataset.stageRoot = "";
        stageShadow.appendChild(stageRoot);
        const mounted = createMountedStage(module, hostRoot, session);
        try {
            await module.mount(createStageContext({
                appName: entry.appName,
                navigate,
                root: stageRoot,
                session,
                stageLoad: options.stageLoad,
                version: entry.manifest.version,
            }));
            if (!canCommit()) {
                throw new DomainError({ code: "conflict", message: "A newer stage route replaced this load" });
            }
            root.replaceChildren(hostRoot);
            return mounted;
        }
        catch (error) {
            await mounted.unmount();
            throw normalizeMountError(appName, error);
        }
    }
    async #importModule(entryUrl) {
        let module;
        try {
            module = await import(/* @vite-ignore */ entryUrl);
        }
        catch {
            // #1529: the never-cached-while-offline case is now caught earlier and deeper than
            // this dynamic import — `client/adapters-web/src/cache/stage-cache.ts`'s
            // `VerifiedStageCache#prepare` (running in the kernel Worker, invoked via
            // `this.#client.prepareStage()` above) fetches+verifies+caches the bundle bytes BEFORE
            // this `#importModule` ever runs, throwing a `TransportError({code: "unavailable"})`
            // there when the underlying fetch fails while offline (see that file's own doc
            // comment). So `entryUrl` here always resolves from an already-verified cache entry
            // once `prepareStage()` has succeeded, and a rejection at THIS import can only be a
            // genuine bundle problem (a corrupt cache entry, a real syntax error) — stays a
            // `DomainError`, never offline-classified. (An earlier, shallower version of this fix
            // detected offline via `navigator.onLine` at this exact catch site — browser I/O client/
            // ui may not touch directly — before the deeper stage-cache.ts fix superseded it.)
            throw new DomainError({
                code: "not_found",
                message: "The requested stage bundle could not be opened",
            });
        }
        if (!isStageModule(module)) {
            throw new DomainError({
                code: "validation_failed",
                message: "The loaded stage module does not implement the required host contract",
            });
        }
        return module;
    }
}
function createMountedStage(module, hostRoot, session) {
    let unmounted = false;
    return {
        unmount: async () => {
            if (unmounted) {
                return;
            }
            unmounted = true;
            session.close();
            hostRoot.remove();
            try {
                void Promise.resolve(module.unmount()).catch(() => undefined);
            }
            catch {
                // Stage-owned teardown cannot retain its revoked session or block the next route.
            }
        },
    };
}
function isStageModule(value) {
    return (typeof value === "object" &&
        value !== null &&
        "mount" in value &&
        typeof value.mount === "function" &&
        "unmount" in value &&
        typeof value.unmount === "function");
}
function normalizeMountError(appName, error) {
    if (error instanceof DomainError) {
        return error;
    }
    return new DomainError({
        code: "invalid_request",
        message: `Stage ${appName} could not be opened`,
    });
}
//# sourceMappingURL=stage-loader.js.map